/**
 * useSupportChat
 * Estado da própria conversa de suporte do usuário logado — usado pelo SupportChatWidget.
 *
 * @version 2025.1.0
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSnackbar } from 'notistack';
import { getSocket } from '../services/socket';
import {
  supportChatService,
  type SupportConversation,
  type SupportConversationSubject,
  type SupportFeedbackTag,
  type SupportMessage,
} from '../services/supportChatService';

export function useSupportChat(enabled: boolean) {
  const { enqueueSnackbar } = useSnackbar();
  const [conversation, setConversation] = useState<SupportConversation | null>(null);
  const [messages, setMessages] = useState<SupportMessage[]>([]);
  const [responsibleName, setResponsibleName] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [socketConnected, setSocketConnected] = useState(false);
  const loadedRef = useRef(false);

  const load = useCallback(async () => {
    try {
      const { conversation: conv, messages: msgs, responsibleName: nome } =
        await supportChatService.getOwnConversation();
      setConversation(conv);
      setMessages(msgs);
      setResponsibleName(nome ?? null);
    } catch {
      enqueueSnackbar('Não foi possível carregar o chat de suporte.', { variant: 'error' });
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    if (!enabled || loadedRef.current) return;
    loadedRef.current = true;
    void load();
  }, [enabled, load]);

  const send = useCallback(
    async (text: string, subject?: SupportConversationSubject): Promise<boolean> => {
      setIsLoading(true);
      try {
        await supportChatService.sendOwnMessage(text, subject);
        await load();
        return true;
      } catch {
        enqueueSnackbar('Não foi possível enviar a mensagem.', { variant: 'error' });
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [load, enqueueSnackbar]
  );

  // Socket.IO — sala da própria conversa, com fallback de polling
  useEffect(() => {
    if (!enabled || !conversation) return;

    const socket = getSocket();
    const room = conversation.id;

    const join = () => {
      socket.emit('join:support-conversation', room);
      setSocketConnected(true);
    };
    const onDisconnect = () => setSocketConnected(false);
    const onMessage = () => void load();

    socket.on('connect', join);
    socket.on('disconnect', onDisconnect);
    socket.on('support:message', onMessage);
    if (socket.connected) join();

    return () => {
      socket.off('connect', join);
      socket.off('disconnect', onDisconnect);
      socket.off('support:message', onMessage);
      socket.emit('leave:support-conversation', room);
    };
  }, [enabled, conversation, load]);

  useEffect(() => {
    if (!enabled || !conversation) return;
    const interval = socketConnected ? 60000 : 10000;
    const timer = setInterval(() => void load(), interval);
    return () => clearInterval(timer);
  }, [enabled, conversation, socketConnected, load]);

  const submitFeedback = useCallback(
    async (tags: SupportFeedbackTag[]) => {
      if (!conversation || tags.length === 0) return;
      try {
        await supportChatService.submitFeedback(conversation.id, tags);
        await load();
        enqueueSnackbar('Obrigado pelo feedback!', { variant: 'success' });
      } catch {
        enqueueSnackbar('Não foi possível registrar a avaliação.', { variant: 'error' });
      }
    },
    [conversation, load, enqueueSnackbar]
  );

  return { conversation, messages, responsibleName, isLoading, send, submitFeedback };
}
